import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "../../lib/api";

const CATEGORIES = [
  "jdm", "collision", "insurance", "custom_paint", "color_change",
  "restoration", "drift", "race", "personal", "specialty",
];

const mediaUrl = (u) => (u?.startsWith("/api/") ? `${API.replace("/api", "")}${u}` : u);

// Multi-file image uploader, returns a list of { url, filename, size, original_size } to the caller.
function ImageUploader({ label, projectId, multiple = true, onUploaded, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savings, setSavings] = useState("");

  const pick = () => inputRef.current?.click();
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr("");
    setSavings("");
    setBusy(true);
    try {
      const results = [];
      let totalOrig = 0, totalOpt = 0;
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("project_id", projectId || "unassigned");
        const { data } = await api.post("/admin/gallery/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        results.push({ ...data, filename: file.name });
        totalOrig += data.original_size || 0;
        totalOpt += data.size || 0;
      }
      if (totalOrig > 0) {
        const kb = (n) => `${(n / 1024).toFixed(0)} KB`;
        const pct = Math.max(0, Math.round((1 - totalOpt / totalOrig) * 100));
        setSavings(`Optimized: ${kb(totalOrig)} → ${kb(totalOpt)} (−${pct}%)`);
      }
      onUploaded(results);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        multiple={multiple}
        onChange={upload}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        data-testid={testid}
        className="btn justify-center disabled:opacity-50"
      >
        {busy ? "Uploading…" : label}
      </button>
      {savings && <span className="text-chrome-300 text-xs">{savings}</span>}
      {err && <span className="text-proform-red text-xs">{err}</span>}
    </div>
  );
}

// A grid of removable thumbnails for a before/during/after array
function ImageGrid({ urls, onRemove, testid }) {
  if (!urls || urls.length === 0) {
    return <div className="text-chrome-300 text-xs italic">None yet</div>;
  }
  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testid}>
      {urls.map((u, i) => (
        <div key={`${u}-${i}`} className="relative aspect-square bg-ink-800 overflow-hidden group">
          <img src={mediaUrl(u)} alt="" loading="lazy" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onRemove(i)}
            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-ink-950/85 text-chrome-100 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs border border-chrome-100/30 hover:border-proform-red hover:text-proform-red"
            aria-label={`Remove image ${i + 1}`}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

const emptyProject = () => ({
  title: "",
  vehicle_year: "",
  make: "",
  model: "",
  chassis_code: "",
  category: "jdm",
  summary: "",
  story: "",
  featured_image: "",
  before_images: [],
  during_images: [],
  after_images: [],
  status: "draft",
  featured: false,
  sort_order: 0,
});

export default function GalleryCMS({ projects, onChange }) {
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState(emptyProject());

  const publishedCount = useMemo(
    () => projects.filter((p) => p.status === "published").length,
    [projects]
  );

  const patch = async (id, changes) => {
    await api.patch(`/admin/projects/${id}`, changes);
    onChange && onChange();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project? Images stay in storage but the project is gone.")) return;
    await api.delete(`/admin/projects/${id}`);
    if (openId === id) setOpenId(null);
    onChange && onChange();
  };

  const createProject = async (e) => {
    e.preventDefault();
    if (!newForm.title) return;
    const { data } = await api.post("/admin/projects", newForm);
    setNewForm(emptyProject());
    setCreating(false);
    onChange && onChange();
    setOpenId(data.id); // open editor immediately for image upload
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display uppercase text-3xl">Gallery Projects</h1>
          <p className="text-chrome-300 text-sm mt-1">
            {projects.length} total · {publishedCount} live on the public gallery
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="btn btn-primary"
          data-testid="gallery-new-project"
        >
          {creating ? "Cancel" : "+ New project"}
        </button>
      </div>

      <div className="card p-4 mb-6 text-sm text-chrome-300">
        <div className="label mb-2">How this works</div>
        Each project has a <span className="text-chrome-100">featured image</span> (used on the /gallery grid and project card) plus three optional galleries: <span className="text-chrome-100">Before</span>, <span className="text-chrome-100">During</span>, <span className="text-chrome-100">After</span>. All uploads are auto-resized + converted to WebP server-side. Set status to <span className="text-chrome-100">published</span> to make a project live on the public gallery.
      </div>

      {creating && (
        <form onSubmit={createProject} className="card p-5 mb-6 grid md:grid-cols-2 gap-3">
          <input required placeholder="Project title *" className="field md:col-span-2" value={newForm.title}
                 onChange={(e) => setNewForm((f) => ({ ...f, title: e.target.value }))} data-testid="new-project-title" />
          <input placeholder="Year" className="field" value={newForm.vehicle_year}
                 onChange={(e) => setNewForm((f) => ({ ...f, vehicle_year: e.target.value }))} />
          <input placeholder="Make" className="field" value={newForm.make}
                 onChange={(e) => setNewForm((f) => ({ ...f, make: e.target.value }))} />
          <input placeholder="Model" className="field" value={newForm.model}
                 onChange={(e) => setNewForm((f) => ({ ...f, model: e.target.value }))} />
          <select className="field" value={newForm.category}
                  onChange={(e) => setNewForm((f) => ({ ...f, category: e.target.value }))}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <textarea placeholder="Short summary (shows on the gallery card)" className="field md:col-span-2" rows={2}
                    value={newForm.summary} onChange={(e) => setNewForm((f) => ({ ...f, summary: e.target.value }))} />
          <div className="md:col-span-2 flex justify-end">
            <button className="btn btn-primary" data-testid="new-project-create">Create project → open editor</button>
          </div>
        </form>
      )}

      <ul className="space-y-3" data-testid="gallery-list">
        {projects.map((p) => (
          <li key={p.id} className={`border ${openId === p.id ? "border-proform-red" : "border-ink-700"} bg-ink-900`}>
            <div className="flex items-center gap-4 p-3">
              <div className="w-24 h-16 bg-ink-800 flex-shrink-0 overflow-hidden">
                {p.featured_image ? (
                  <img src={mediaUrl(p.featured_image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-chrome-300 text-[10px]">No image</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg truncate">{p.title || "Untitled"}</div>
                <div className="label truncate">
                  {p.category} · {p.vehicle_year} {p.make} {p.model}
                  {p.featured && <span className="ml-2 text-proform-red">FEATURED</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={p.featured}
                         onChange={(e) => patch(p.id, { featured: e.target.checked })}
                         className="accent-proform-red" />
                  <span className="label">Featured</span>
                </label>
                <select
                  value={p.status}
                  onChange={(e) => patch(p.id, { status: e.target.value })}
                  className="field py-1 text-xs"
                  data-testid={`gallery-status-${p.id}`}
                >
                  <option value="draft">draft</option>
                  <option value="published">published</option>
                </select>
                <button className="btn" onClick={() => setOpenId(openId === p.id ? null : p.id)}
                        data-testid={`gallery-edit-${p.id}`}>
                  {openId === p.id ? "Close" : "Edit"}
                </button>
                <button className="label hover:text-proform-red" onClick={() => remove(p.id)}
                        data-testid={`gallery-delete-${p.id}`}>Delete</button>
              </div>
            </div>

            {openId === p.id && (
              <ProjectEditor project={p} onPatch={(changes) => patch(p.id, changes)} />
            )}
          </li>
        ))}
        {projects.length === 0 && (
          <li className="text-chrome-300 text-sm p-4 border border-dashed border-ink-700">
            No projects yet. Click <span className="text-chrome-100">+ New project</span> to create one, then upload photos inside the editor.
          </li>
        )}
      </ul>
    </div>
  );
}

function ProjectEditor({ project, onPatch }) {
  const [local, setLocal] = useState(project);
  useEffect(() => { setLocal(project); }, [project.id]);

  const setLocalOnly = (changes) => setLocal((p) => ({ ...p, ...changes }));
  const commit = (changes) => {
    setLocal((p) => ({ ...p, ...changes }));
    onPatch(changes);
  };

  const appendImages = (field) => (uploads) => {
    const urls = uploads.map((u) => u.url);
    const next = [...(local[field] || []), ...urls];
    commit({ [field]: next });
  };

  const removeImage = (field) => (idx) => {
    const next = (local[field] || []).filter((_, i) => i !== idx);
    commit({ [field]: next });
  };

  const setFeatured = (uploads) => {
    if (!uploads.length) return;
    commit({ featured_image: uploads[0].url });
  };

  return (
    <div className="border-t border-ink-700 p-4 md:p-6 grid md:grid-cols-2 gap-6 bg-ink-950/60">
      {/* Left: metadata */}
      <div className="space-y-4">
        <div>
          <label className="field-label">Title</label>
          <input className="field" value={local.title || ""}
                 onChange={(e) => setLocalOnly({ title: e.target.value })}
                 onBlur={(e) => commit({ title: e.target.value })} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="field-label">Year</label>
            <input className="field" value={local.vehicle_year || ""}
                   onChange={(e) => setLocalOnly({ vehicle_year: e.target.value })}
                   onBlur={(e) => commit({ vehicle_year: e.target.value })} />
          </div>
          <div>
            <label className="field-label">Make</label>
            <input className="field" value={local.make || ""}
                   onChange={(e) => setLocalOnly({ make: e.target.value })}
                   onBlur={(e) => commit({ make: e.target.value })} />
          </div>
          <div>
            <label className="field-label">Model</label>
            <input className="field" value={local.model || ""}
                   onChange={(e) => setLocalOnly({ model: e.target.value })}
                   onBlur={(e) => commit({ model: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="field-label">Category</label>
          <select className="field" value={local.category || "jdm"}
                  onChange={(e) => commit({ category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Summary (shows on gallery card)</label>
          <textarea className="field" rows={2} value={local.summary || ""}
                    onChange={(e) => setLocalOnly({ summary: e.target.value })}
                    onBlur={(e) => commit({ summary: e.target.value })} />
        </div>
        <div>
          <label className="field-label">Story (shows on project detail page)</label>
          <textarea className="field" rows={4} value={local.story || ""}
                    onChange={(e) => setLocalOnly({ story: e.target.value })}
                    onBlur={(e) => commit({ story: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label">Sort order</label>
            <input type="number" className="field" value={local.sort_order || 0}
                   onChange={(e) => setLocalOnly({ sort_order: Number(e.target.value) })}
                   onBlur={(e) => commit({ sort_order: Number(e.target.value) })} />
          </div>
          <div>
            <label className="field-label">Instagram URL</label>
            <input className="field" value={local.instagram_url || ""}
                   onChange={(e) => setLocalOnly({ instagram_url: e.target.value })}
                   onBlur={(e) => commit({ instagram_url: e.target.value })} />
          </div>
        </div>
      </div>

      {/* Right: image uploads */}
      <div className="space-y-6">
        <div>
          <div className="label mb-2">Featured image (single)</div>
          {local.featured_image ? (
            <div className="relative aspect-video bg-ink-800 mb-2 overflow-hidden">
              <img src={mediaUrl(local.featured_image)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => commit({ featured_image: "" })}
                      className="absolute top-2 right-2 h-7 w-7 rounded-full bg-ink-950/85 text-chrome-100 border border-chrome-100/30 hover:border-proform-red hover:text-proform-red flex items-center justify-center text-sm">
                ×
              </button>
            </div>
          ) : (
            <div className="text-chrome-300 text-xs italic mb-2">No featured image yet</div>
          )}
          <ImageUploader
            label={local.featured_image ? "Replace featured image" : "+ Upload featured image"}
            projectId={project.id}
            multiple={false}
            onUploaded={setFeatured}
            testid={`gallery-upload-featured-${project.id}`}
          />
        </div>

        <div>
          <div className="label mb-2">Before ({(local.before_images || []).length})</div>
          <ImageGrid urls={local.before_images} onRemove={removeImage("before_images")} testid={`gallery-before-grid-${project.id}`} />
          <div className="mt-2">
            <ImageUploader
              label="+ Bulk upload BEFORE images"
              projectId={project.id}
              onUploaded={appendImages("before_images")}
              testid={`gallery-upload-before-${project.id}`}
            />
          </div>
        </div>

        <div>
          <div className="label mb-2">During ({(local.during_images || []).length})</div>
          <ImageGrid urls={local.during_images} onRemove={removeImage("during_images")} testid={`gallery-during-grid-${project.id}`} />
          <div className="mt-2">
            <ImageUploader
              label="+ Bulk upload DURING images"
              projectId={project.id}
              onUploaded={appendImages("during_images")}
              testid={`gallery-upload-during-${project.id}`}
            />
          </div>
        </div>

        <div>
          <div className="label mb-2">After ({(local.after_images || []).length})</div>
          <ImageGrid urls={local.after_images} onRemove={removeImage("after_images")} testid={`gallery-after-grid-${project.id}`} />
          <div className="mt-2">
            <ImageUploader
              label="+ Bulk upload AFTER images"
              projectId={project.id}
              onUploaded={appendImages("after_images")}
              testid={`gallery-upload-after-${project.id}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
